'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronRightGlyph } from '../../../internal/icons.js';
import { addDays, addMonths, addYears, clampIso, endOfMonth, endOfWeek, formatDayNumber, formatFullDate, formatMonthTitle, isIsoDate, isoWeekNumber, isSameMonth, isWithin, localeWeekStart, monthGrid, startOfMonth, startOfWeek, useToday, weekdayNames, } from '../../../internal/dates/index.js';
const DEFAULT_LABELS = {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    week: 'Week',
    weekNumber: (n) => `Week ${n}`,
};
function isRtl(el) {
    if (!el)
        return false;
    const dir = el.closest('[dir]')?.getAttribute('dir');
    if (dir === 'rtl' || dir === 'ltr')
        return dir === 'rtl';
    return typeof getComputedStyle !== 'undefined' && getComputedStyle(el).direction === 'rtl';
}
/**
 * A month calendar grid with full keyboard support, following the WAI-ARIA
 * APG date-picker grid: arrow keys move by day and week, Home and End to the
 * week's ends, PageUp and PageDown by month (with Shift, by year), Enter or
 * Space selects. One roving Tab stop; `aria-selected` on the chosen days and
 * `aria-current="date"` on today. The foundation of DatePicker and
 * DateRangePicker, and usable on its own as a sidebar calendar.
 */
export const MiniMonth = forwardRef(function MiniMonth({ value: valueProp, defaultValue = null, onValueChange, month: monthProp, defaultMonth, onMonthChange, range, rangePreview, onDayHover, min, max, isDateDisabled, getDayInfo, today: todayProp, locale, weekStartsOn: weekStartsOnProp, showWeekNumbers = false, showOutsideDays = true, fixedWeeks = true, numberOfMonths = 1, focusOnMount = false, labels: labelsProp, footer, className, onMouseLeave, ...rest }, ref) {
    const labels = { ...DEFAULT_LABELS, ...labelsProp };
    const baseId = useId();
    const rootRef = useRef(null);
    const today = useToday(todayProp);
    const weekStartsOn = weekStartsOnProp ?? localeWeekStart(locale);
    const count = Math.max(1, Math.floor(numberOfMonths));
    const [innerValue, setInnerValue] = useState(defaultValue);
    const value = valueProp !== undefined ? valueProp : innerValue;
    const anchor = range?.start ?? value ?? null;
    const [innerMonth, setInnerMonth] = useState(() => defaultMonth && isIsoDate(defaultMonth) ? startOfMonth(defaultMonth) : null);
    const firstMonth = startOfMonth(clampIso((monthProp && isIsoDate(monthProp) ? monthProp : null) ??
        innerMonth ??
        (anchor && isIsoDate(anchor) ? anchor : today), min ? startOfMonth(min) : null, max ? startOfMonth(addMonths(startOfMonth(max), -(count - 1))) : null));
    const lastMonth = addMonths(firstMonth, count - 1);
    const visibleEnd = endOfMonth(lastMonth);
    const setMonth = useCallback((next) => {
        const first = startOfMonth(next);
        if (monthProp === undefined)
            setInnerMonth(first);
        onMonthChange?.(first);
    }, [monthProp, onMonthChange]);
    const [focused, setFocused] = useState(null);
    const pendingFocus = useRef(false);
    const isDisabled = useCallback((d) => !isWithin(d, min, max) || !!isDateDisabled?.(d), [min, max, isDateDisabled]);
    const inView = (d) => !!d && isIsoDate(d) && d >= firstMonth && d <= visibleEnd;
    // The roving Tab stop: the focused day, else the selection, else today, else the first enabled day.
    const tabStop = useMemo(() => {
        for (const c of [focused, value, range?.start, today])
            if (inView(c))
                return c;
        let d = firstMonth;
        while (d <= visibleEnd && isDisabled(d))
            d = addDays(d, 1);
        return d <= visibleEnd ? d : firstMonth;
        // inView is derived from firstMonth and visibleEnd.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focused, value, range?.start, today, firstMonth, visibleEnd, isDisabled]);
    const focusDay = useCallback((d) => {
        rootRef.current?.querySelector(`[data-date="${d}"]`)?.focus();
    }, []);
    useImperativeHandle(ref, () => rootRef.current);
    useEffect(() => {
        if (!pendingFocus.current || !focused)
            return;
        pendingFocus.current = false;
        focusDay(focused);
    }, [focused, firstMonth, focusDay]);
    useEffect(() => {
        if (focusOnMount)
            focusDay(tabStop);
        // Only on mount: opening a popover focuses the current day once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const moveFocus = (target) => {
        const t = clampIso(target, min, max);
        if (t < firstMonth)
            setMonth(t);
        else if (t > visibleEnd)
            setMonth(addMonths(startOfMonth(t), -(count - 1)));
        pendingFocus.current = true;
        setFocused(t);
        onDayHover?.(t);
    };
    const select = (d) => {
        if (isDisabled(d))
            return;
        if (valueProp === undefined && !range)
            setInnerValue(d);
        setFocused(d);
        onValueChange?.(d);
    };
    const onKeyDown = (event) => {
        const d = event.target.dataset?.date;
        if (!d)
            return;
        const rtl = isRtl(rootRef.current);
        const back = rtl ? 1 : -1;
        let target = null;
        switch (event.key) {
            case 'ArrowLeft':
                target = addDays(d, back);
                break;
            case 'ArrowRight':
                target = addDays(d, -back);
                break;
            case 'ArrowUp':
                target = addDays(d, -7);
                break;
            case 'ArrowDown':
                target = addDays(d, 7);
                break;
            case 'Home':
                target = startOfWeek(d, weekStartsOn);
                break;
            case 'End':
                target = endOfWeek(d, weekStartsOn);
                break;
            case 'PageUp':
                target = event.shiftKey ? addYears(d, -1) : addMonths(d, -1);
                break;
            case 'PageDown':
                target = event.shiftKey ? addYears(d, 1) : addMonths(d, 1);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                select(d);
                return;
            default:
                return;
        }
        event.preventDefault();
        moveFocus(target);
    };
    const step = (delta) => {
        const next = addMonths(firstMonth, delta);
        setMonth(next);
        // The Tab stop follows to the same day in the new month, without moving focus off the button.
        const base = focused ?? tabStop;
        setFocused(clampIso(addMonths(base, delta), min, max));
    };
    const canPrev = !min || endOfMonth(addMonths(firstMonth, -1)) >= min;
    const canNext = !max || addMonths(lastMonth, 1) <= max;
    const longNames = weekdayNames(locale, weekStartsOn, 'long');
    const shortNames = weekdayNames(locale, weekStartsOn, 'short');
    const inRange = (r, d) => {
        if (!r?.start)
            return { start: false, end: false, within: false };
        const end = r.end ?? r.start;
        const lo = r.start <= end ? r.start : end;
        const hi = r.start <= end ? end : r.start;
        return { start: d === lo, end: d === hi, within: d >= lo && d <= hi };
    };
    const months = Array.from({ length: count }, (_, i) => addMonths(firstMonth, i));
    return (
    // Pointer leave only clears the hover preview; keyboard users get the same through focus.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    _jsxs("div", { ref: rootRef, ...rest, className: cx('mn-mini-month', className), "data-months": count > 1 ? count : undefined, "data-week-numbers": showWeekNumbers || undefined, onMouseLeave: (e) => {
            onMouseLeave?.(e);
            onDayHover?.(null);
        }, children: [_jsx("div", { className: "mn-mini-month__months", children: months.map((m, index) => {
                    const titleId = `${baseId}-title-${index}`;
                    const weeks = monthGrid(m, weekStartsOn, fixedWeeks);
                    const outside = showOutsideDays && count === 1;
                    return (_jsxs("div", { className: "mn-mini-month__month", children: [_jsxs("div", { className: "mn-mini-month__header", children: [index === 0 ? (_jsx("button", { type: "button", className: "mn-mini-month__nav", "data-direction": "previous", "aria-label": labels.previousMonth, disabled: !canPrev, onClick: () => step(-1), children: _jsx(ChevronRightGlyph, {}) })) : (_jsx("span", { className: "mn-mini-month__nav-spacer" })), _jsx("span", { id: titleId, className: "mn-mini-month__title", "aria-live": index === 0 ? 'polite' : undefined, children: formatMonthTitle(m, locale) }), index === count - 1 ? (_jsx("button", { type: "button", className: "mn-mini-month__nav", "data-direction": "next", "aria-label": labels.nextMonth, disabled: !canNext, onClick: () => step(1), children: _jsx(ChevronRightGlyph, {}) })) : (_jsx("span", { className: "mn-mini-month__nav-spacer" }))] }), _jsxs("table", { 
                                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                                role: "grid", className: "mn-mini-month__grid", "aria-labelledby": titleId, onKeyDown: onKeyDown, children: [_jsx("thead", { children: _jsxs("tr", { children: [showWeekNumbers ? (_jsx("th", { scope: "col", className: "mn-mini-month__weekday mn-mini-month__week-head", children: _jsx("span", { className: "mn-visually-hidden", children: labels.week }) })) : null, shortNames.map((name, i) => (_jsx("th", { scope: "col", abbr: longNames[i], className: "mn-mini-month__weekday", children: name }, i)))] }) }), _jsx("tbody", { children: weeks.map((week) => {
                                            const weekNo = isoWeekNumber(week[3] ?? week[0] ?? m);
                                            return (_jsxs("tr", { children: [showWeekNumbers ? (_jsx("th", { scope: "row", className: "mn-mini-month__week", "aria-label": labels.weekNumber(weekNo), children: weekNo })) : null, week.map((d) => {
                                                        if (!isSameMonth(d, m))
                                                            return (_jsx("td", { className: "mn-mini-month__cell", "data-outside": "", children: outside ? (_jsx("span", { className: "mn-mini-month__outside", "aria-hidden": true, children: formatDayNumber(d, locale) })) : null }, d));
                                                        const r = inRange(range, d);
                                                        const p = inRange(rangePreview, d);
                                                        const selected = range ? r.within : d === value;
                                                        const disabled = isDisabled(d);
                                                        const info = getDayInfo?.(d);
                                                        const dots = info?.dots?.slice(0, 3) ?? [];
                                                        const name = [formatFullDate(d, locale), info?.description]
                                                            .filter(Boolean)
                                                            .join(', ');
                                                        return (_jsx("td", { className: "mn-mini-month__cell", "aria-selected": selected, "aria-disabled": disabled || undefined, "data-in-range": (range && r.within) || undefined, "data-range-start": (range && r.start) || undefined, "data-range-end": (range && r.end) || undefined, "data-preview": p.within || undefined, "data-preview-start": p.start || undefined, "data-preview-end": p.end || undefined, children: _jsxs("button", { type: "button", className: "mn-mini-month__day", "data-date": d, tabIndex: d === tabStop ? 0 : -1, "aria-label": name, "aria-current": d === today ? 'date' : undefined, "aria-disabled": disabled || undefined, "data-selected": (range ? r.start || r.end : d === value) || undefined, "data-today": d === today || undefined, "data-disabled": disabled || undefined, "data-unavailable": (disabled && isWithin(d, min, max)) || undefined, onClick: () => select(d), onFocus: () => {
                                                                    if (focused !== d)
                                                                        setFocused(d);
                                                                }, onMouseEnter: () => onDayHover?.(d), children: [_jsx("span", { className: "mn-mini-month__number", children: formatDayNumber(d, locale) }), dots.length ? (_jsx("span", { className: "mn-mini-month__dots", "aria-hidden": true, children: dots.map((tone, i) => (_jsx("span", { className: "mn-mini-month__dot", "data-tone": tone }, i))) })) : null] }) }, d));
                                                    })] }, week[0]));
                                        }) })] })] }, m));
                }) }), footer ? _jsx("div", { className: "mn-mini-month__footer", children: footer }) : null] }));
});
//# sourceMappingURL=mini-month.js.map